// The job resource (`SC-F332`, frozen interface `I7`, `06` §1.8) and the ONE
// progress helper the whole UI uses.
//
// Instance provisioning, publishing, rollback, deletion, upgrades, every
// proxied CLI command, audit verification and audit export are asynchronous and
// they all produce the *same* resource. `I7` is frozen precisely so the UI does
// not grow five progress patterns: whatever started the work, a screen watches
// it with [watchJob] and renders [Job.progress]. If you find yourself writing a
// second poll loop, the job resource is the thing you are meant to poll.
//
// Long-poll, not fixed-interval polling: `?wait=<seconds>` (30 maximum) holds
// the request open until the job reaches a terminal state, which is what makes
// the fast cases — a `config.validate` that finishes in 1.5 s — feel synchronous
// without a second round trip (`06` §1.8).

import { ApiError, type ErrorEnvelope } from "./errors.js";
import { getJSON, query, request, type Page, type PageParams } from "./http.js";

const BASE = "/v1/jobs";

/**
 * JobType is the closed, dotted vocabulary of `06` §1.8. It is what decides a
 * job's `result` schema, its retention (30 days for lifecycle jobs, 90 for
 * `policy.test` and `audit.verify` because they are evidence), whether it takes
 * the instance's serialization lock, and — via the *inherited* rule in `06`
 * §3.11 — which scoped-token feature may watch it.
 */
export type JobType =
  | "instance.create"
  | "instance.delete"
  | "instance.restart"
  | "instance.suspend"
  | "instance.resume"
  | "instance.upgrade"
  | "config.publish"
  | "config.rollback"
  | "config.validate"
  | "policy.validate"
  | "policy.explain"
  | "policy.test"
  | "datasource.check"
  | "datasource.probe"
  | "audit.verify"
  | "audit.export"
  | "schema.introspect";

/**
 * JOB_TYPES is `06` §1.8's vocabulary in its own order — lifecycle, then config,
 * then the proxied commands, then the audit and schema jobs.
 *
 * It exists so a filter picker is built from the vocabulary rather than from
 * whatever a page remembered, and it is deliberately ordered rather than sorted
 * alphabetically: a reader scanning a type filter is looking for a *kind* of
 * operation, and the dotted prefixes already group them.
 */
export const JOB_TYPES: readonly JobType[] = [
  "instance.create",
  "instance.delete",
  "instance.restart",
  "instance.suspend",
  "instance.resume",
  "instance.upgrade",
  "config.publish",
  "config.rollback",
  "config.validate",
  "policy.validate",
  "policy.explain",
  "policy.test",
  "datasource.check",
  "datasource.probe",
  "audit.verify",
  "audit.export",
  "schema.introspect",
];

/**
 * JOB_TYPE_LABELS is the display word for each type, so the job history, the
 * single-job page and an instance's operations panel cannot spell one type three
 * ways.
 *
 * The labels name the OPERATION rather than transliterating the token: `config.publish`
 * is "Publish" because that is what the reader did, and `instance.upgrade` is
 * "Size or version change" because that is what the type actually carries —
 * `06` §1.8's vocabulary is closed and has no `instance.resize`, so a size change
 * is submitted as an upgrade with `params.changed` naming the fields that moved.
 * A label reading "Upgrade" on a row that changed a size class would send a reader
 * looking for a version that never changed.
 */
export const JOB_TYPE_LABELS: Record<string, string> = {
  "instance.create": "Provision instance",
  "instance.delete": "Delete instance",
  "instance.restart": "Restart instance",
  "instance.suspend": "Suspend instance",
  "instance.resume": "Resume instance",
  "instance.upgrade": "Size or version change",
  "config.publish": "Publish",
  "config.rollback": "Roll forward to an older generation",
  "config.validate": "sluice config validate",
  "policy.validate": "sluice policy validate",
  "policy.explain": "sluice policy explain",
  "policy.test": "sluice policy test",
  "datasource.check": "Datasource check",
  "datasource.probe": "Datasource probe",
  "audit.verify": "sluice audit verify",
  "audit.export": "Audit export",
  "schema.introspect": "Schema introspection",
};

/** JobStatus is `queued` → `running` → one of four terminal states. Terminal
 * states are final: a job never leaves one. */
export type JobStatus = "queued" | "running" | "succeeded" | "failed" | "cancelled" | "timed_out";

/** JOB_STATUSES is the six states in lifecycle order: the two live ones first,
 * then the four terminal ones. */
export const JOB_STATUSES: readonly JobStatus[] = [
  "queued",
  "running",
  "succeeded",
  "failed",
  "cancelled",
  "timed_out",
];

/**
 * JOB_STATUS_LABELS is the display word for each state.
 *
 * `timed_out` is spelled "Timed out" and not "Failed", and the distinction is
 * load-bearing: it means the reaper found the job with no live worker behind it and
 * released the instance lock, which is a statement about the platform rather than
 * about the operation. Folding it into "failed" would lose the only signal that
 * distinguishes a rejected change from a dead worker.
 */
export const JOB_STATUS_LABELS: Record<string, string> = {
  queued: "Queued",
  running: "Running",
  succeeded: "Succeeded",
  failed: "Failed",
  cancelled: "Cancelled",
  timed_out: "Timed out",
};

/** JobProgress is the coarse step counter a progress bar renders. `step` is a
 * short machine name (`await-digest`, `render-bundle`); `index` is 1-based. */
export interface JobProgress {
  step: string;
  index: number;
  total: number;
}

/** JobActor names who started the job, so an instance's job history reads as an
 * operations log rather than a list of anonymous events. */
export interface JobActor {
  kind: "session" | "api_key" | "agent" | "system";
  account?: string;
  key_id?: string;
}

/**
 * jobActorLabel renders an actor as one readable phrase.
 *
 * The `kind` is kept even where an identity is present, because "by API key
 * `pat_3kd`" and "by ana@acme.example" are different facts about how a change was
 * made: `SC-S811` requires an entry produced by a fine-grained token to name both
 * the token and its owning account, and a label that showed only the account would
 * make an automated publish indistinguishable from a human one.
 *
 * `system` is rendered as "Sluice Cloud" rather than left blank — a change nobody
 * signed is precisely the kind a reader should be told about.
 */
export function jobActorLabel(actor: JobActor): string {
  switch (actor.kind) {
    case "session":
      return actor.account ? `${actor.account} (signed-in session)` : "a signed-in session";
    case "api_key":
      return actor.key_id
        ? `API key ${actor.key_id}${actor.account ? ` (owned by ${actor.account})` : ""}`
        : "an API key";
    case "agent":
      return actor.account ? `${actor.account} (agent)` : "an agent";
    case "system":
      return "Sluice Cloud (system)";
    default:
      return actor.kind;
  }
}

/**
 * Job is the `06` §1.8 resource. `result` is populated only on `succeeded` and
 * its schema is keyed by `type` — hence the type parameter, which each domain
 * module fills in with its own result shape.
 *
 * `error` is the `06` §1.4 envelope **minus `request_id`**: a job outlives the
 * request that created it, and the id of that request would be misleading by
 * the time anyone reads the failure (`internal/apierr/apierr.go`, `05` §3.13).
 */
export interface Job<R = unknown> {
  id: string;
  type: JobType;
  status: JobStatus;
  org: string;
  /** Null for the few organization-scoped jobs; a record id otherwise. */
  instance: string | null;
  created: string;
  started: string | null;
  finished: string | null;
  progress: JobProgress | null;
  actor: JobActor;
  idempotency_key: string | null;
  result: R | null;
  error: Omit<ErrorEnvelope, "request_id"> | null;
}

/**
 * JobStarted is the body of a `202 Accepted` from any operation that cannot
 * complete inside one request. Every such route answers it, alongside a
 * `Location: /v1/jobs/{id}` header, and most embed the affected resource next
 * to the job (see `InstanceCreated` in `api/instances.ts`).
 */
export interface JobStarted<R = unknown> {
  job: Job<R>;
}

/**
 * jobRetentionDays is how long a finished job of this type is kept (`05` §6.1).
 *
 * Two values, and the split is the reason this is a function rather than one
 * number: `policy.test` and `audit.verify` are **evidence** — a compliance reviewer
 * asks whether the suite passed before a release, months later — so their rows and
 * their output survive 90 days, while a lifecycle job's row is an operations
 * receipt and 30 days is enough. A permalink has to be able to say which rule
 * applies to the job in front of the reader, because "this job is gone" is only
 * honest with the retention beside it.
 */
export function jobRetentionDays(type: JobType | string): number {
  return type === "policy.test" || type === "audit.verify" ? 90 : 30;
}

/** isTerminal reports whether a job has finished. The four terminal states are
 * final, so this is also "stop polling". */
export function isTerminal(status: JobStatus): boolean {
  return (
    status === "succeeded" ||
    status === "failed" ||
    status === "cancelled" ||
    status === "timed_out"
  );
}

/**
 * jobFailure renders a terminal job's outcome as an [ApiError], or null when the
 * job succeeded. [watchJob] deliberately *resolves* on every terminal state
 * rather than rejecting on failure — a cancelled publish is not an exception,
 * it is an outcome the screen shows — so this is how a caller turns a failed
 * job into the same error object the rest of the UI already handles.
 */
export function jobFailure(job: Job): ApiError | null {
  if (job.status === "succeeded") return null;
  if (job.status === "cancelled") {
    return new ApiError({ code: "job_not_cancellable", message: "The job was cancelled." });
  }
  if (job.error) {
    return new ApiError({
      code: job.error.code,
      message: job.error.message,
      details: job.error.details,
    });
  }
  return new ApiError({
    code: "internal",
    message: job.status === "timed_out" ? "The operation timed out." : "The operation failed.",
  });
}

/**
 * getJob reads one job. `waitSeconds` long-polls: the server holds the request
 * open for up to that many seconds (30 maximum) and answers `200` with the
 * completed job if it finishes inside the window, `202` with its current state
 * otherwise. Either way the parsed body is a [Job].
 */
export function getJob<R = unknown>(
  jobId: string,
  opts: { waitSeconds?: number; signal?: AbortSignal } = {},
): Promise<Job<R>> {
  const q = query({ wait: opts.waitSeconds });
  return getJSON<Job<R>>(`${BASE}/${encodeURIComponent(jobId)}${q}`, { signal: opts.signal });
}

/**
 * cancelJob requests cancellation. `06` §3.11 makes the semantics explicit
 * rather than best-effort: the read-only proxied commands are cancellable at any
 * point, while `config.publish`, `config.rollback` and the `instance.*` types
 * are cancellable only while `queued` — once the orchestrator has begun mutating
 * Swarm state, cancelling would leave desired and observed state divergent and
 * the answer is `409 job_not_cancellable`.
 */
export async function cancelJob(jobId: string): Promise<void> {
  await request(`${BASE}/${encodeURIComponent(jobId)}/cancel`, { method: "POST" });
}

/** JobFilter is the allowlist on an instance's job history (`06` §3.4). */
export interface JobFilter extends PageParams {
  type?: JobType;
  status?: JobStatus;
}

/** listInstanceJobs returns one instance's job history, newest first. */
export function listInstanceJobs(instanceId: string, filter: JobFilter = {}): Promise<Page<Job>> {
  const q = query({ ...filter });
  return getJSON<Page<Job>>(`/v1/instances/${encodeURIComponent(instanceId)}/jobs${q}`);
}

/** WatchJobOptions tunes [watchJob]. The defaults are the ones `06` §1.8 and
 * §1.9 establish; override them only with a reason. */
export interface WatchJobOptions<R = unknown> {
  /** Called on every observation, including the first and the terminal one.
   * This is where a screen updates its progress bar and its step label. */
  onUpdate?: (job: Job<R>) => void;
  /** Seconds to hold each long-poll open. Capped at 30 by the server. */
  waitSeconds?: number;
  /** Gap between polls when the server answers immediately (no `?wait=`
   * support, or a job that is still queued). `06` §1.9 establishes 3000 ms as
   * the poll-fallback value for the whole product. */
  pollMs?: number;
  /** Aborts the watch. The returned promise rejects with the abort. */
  signal?: AbortSignal;
}

/**
 * watchJob is **the** progress helper. It long-polls a job until it reaches a
 * terminal state and resolves with the final record — succeeded, failed,
 * cancelled or timed out alike. Use [jobFailure] to turn a non-success into an
 * error, so that the caller decides whether it is a toast or an inline panel.
 *
 *   const started = await publishDraft(instanceId, body);
 *   const done = await watchJob(started.job.id, {
 *     onUpdate: (j) => (this.progress = j.progress),
 *   });
 *   const failure = jobFailure(done);
 *   if (failure) toastPersistent(failure.message, "error", "Publish failed");
 *
 * Transport failures are tolerated in a bounded way: a long-poll dropped by an
 * intermediary is the expected steady state of a 30-second held request, so a
 * handful of consecutive network errors are retried before the watch gives up.
 * A server *answer* — 403, 404, `job_not_cancellable` — is not retried; it is
 * thrown, because it will not become true by asking again.
 */
export async function watchJob<R = unknown>(
  jobId: string,
  opts: WatchJobOptions<R> = {},
): Promise<Job<R>> {
  const waitSeconds = opts.waitSeconds ?? 20;
  const pollMs = opts.pollMs ?? 3000;
  let transportFailures = 0;

  for (;;) {
    let job: Job<R>;
    try {
      job = await getJob<R>(jobId, { waitSeconds, signal: opts.signal });
      transportFailures = 0;
    } catch (err) {
      // `http.request` turns an aborted fetch back into the DOMException and
      // everything else into an ApiError, so "no status" means the request
      // never got an answer at all.
      if (err instanceof ApiError && err.status === undefined && ++transportFailures <= 3) {
        await sleep(pollMs, opts.signal);
        continue;
      }
      throw err;
    }

    opts.onUpdate?.(job);
    if (isTerminal(job.status)) return job;
    await sleep(pollMs, opts.signal);
  }
}

/** sleep resolves after ms, or rejects when the signal aborts. */
function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason ?? new DOMException("Aborted", "AbortError"));
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(signal?.reason ?? new DOMException("Aborted", "AbortError"));
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}
